import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import secondaryComponents from "../../../../../components/secondaryComponents";
import primaryComponents from "../../../../../components/primaryComponents";
import globalStyles from "../../../../../assets/styles/base/globalPage.module.scss";
import styles from "./ReturnReview.module.scss";
import useReturnReview from "./useReturnReview";
import { formatCurrency } from "../../../../../helpers";
import ReturnReviewActions from "./ReturnReviewActions";
import ReturnDetailsCard from "./ReturnDetailsCard";

const ReturnReview = () => {
  const {
    id,
    returnDetails,
    isLoadingDetails,
    isApproving,
    isRejecting,
    isApproveModalOpen,
    setIsApproveModalOpen,
    isRejectModalOpen,
    setIsRejectModalOpen,
    handleApprove,
    handleReject,
    handleBack,
  } = useReturnReview();

  const status = returnDetails?.status || "REVIEW";
  const isPending =
    status === "REVIEW" || status === "IN_REVIEW" || status === "PENDING";
  const isApproved = status === "APPROVED";
  const isRejected = status === "REJECTED";

  const statusClass = isPending
    ? globalStyles["statusBlock--pending"]
    : isApproved
      ? globalStyles["statusBlock--success"]
      : isRejected
        ? globalStyles["statusBlock--failed"]
        : globalStyles.statusBlock;

  const statusLabel = isPending
    ? "In Review"
    : isApproved
      ? "Approved"
      : isRejected
        ? "Rejected"
        : status;

  const currencyCode = returnDetails?.store?.currency_code || "NGN";
  const orderId = returnDetails?.order?.id || returnDetails?.order;
  const customerName =
    returnDetails?.customer?.full_name ||
    returnDetails?.order?.customer?.full_name;
  const customerId =
    returnDetails?.customer?.id || returnDetails?.order?.customer?.id;
  const submittedByName =
    returnDetails?.submitted_by?.full_name ||
    returnDetails?.cashier?.first_name ||
    "-";
  const totalAmount =
    returnDetails?.total_requested_amount ||
    returnDetails?.total_returned_amount ||
    0;
  const deliveryFee =
    returnDetails?.total_requested_delivery_fee ??
    returnDetails?.total_returned_delivery_fee ??
    0;
  const serviceCharge =
    returnDetails?.total_requested_service_charge ??
    returnDetails?.total_returned_service_charge ??
    0;
  const refundableAmount =
    returnDetails?.refundable_amount ??
    Number(totalAmount) + Number(deliveryFee) + Number(serviceCharge);

  return (
    <secondaryComponents.MetaSetter
      title={`Lumi Merchant | Return Review #${id}`}
      description="Review return request details"
    >
      <secondaryComponents.RequestLoaderTrigger>
        <div className={styles.returnReview}>
          {/* Header & Navigation */}
          <div className={styles.returnReview__header}>
            <div
              className="d-flex align-items-center flex-wrap"
              style={{ gap: "16px" }}
            >
              <primaryComponents.Button
                classNames="btn btn--outline"
                onClick={handleBack}
                type="button"
                style={{ width: "max-content" }}
              >
                <FontAwesomeIcon
                  icon="angle-left"
                  className="mr-2"
                  style={{ fontSize: "16px" }}
                />
                <span>Back</span>
              </primaryComponents.Button>

              <h4 className="mb-0">Return Request Review #{id}</h4>

              <span
                className={`${globalStyles.statusBlock} ${statusClass}`}
                style={{ whiteSpace: "nowrap", fontSize: "12px" }}
              >
                {statusLabel}
              </span>
            </div>

            <ReturnReviewActions
              isPending={isPending}
              isApproving={isApproving}
              isRejecting={isRejecting}
              setIsApproveModalOpen={setIsApproveModalOpen}
              setIsRejectModalOpen={setIsRejectModalOpen}
            />
          </div>

          <secondaryComponents.LoaderHelper
            isLoading={isLoadingDetails}
            classNames="mt-3"
          >
            {/* Rejection Notice if rejected */}
            {isRejected && returnDetails?.rejection_reason && (
              <div className={styles.returnReview__rejectionNotice}>
                <h6>Rejection Reason</h6>
                <p>{returnDetails.rejection_reason}</p>
              </div>
            )}

            {/* Return Details Card */}
            <ReturnDetailsCard
              returnDetails={returnDetails}
              currencyCode={currencyCode}
              orderId={orderId}
              customerName={customerName}
              customerId={customerId}
              submittedByName={submittedByName}
              totalAmount={totalAmount}
              refundableAmount={refundableAmount}
              deliveryFee={deliveryFee}
              serviceCharge={serviceCharge}
              isApproved={isApproved}
              isRejected={isRejected}
            />

            {/* Returned Products Table */}
            <div className={styles.returnReview__tableWrapper}>
              <h5 className="mb-3">Returned Products</h5>
              <secondaryComponents.ReturnItemsTable
                items={returnDetails?.items}
                currencyCode={currencyCode}
              />
            </div>
          </secondaryComponents.LoaderHelper>

          {/* Approve Confirmation Modal */}
          <secondaryComponents.ConfirmDialog
            isOpen={isApproveModalOpen}
            setIsOpen={setIsApproveModalOpen}
            title=""
            confirmText={
              <span>
                <FontAwesomeIcon icon="check" className="mr-2" /> Confirm
                Approval
              </span>
            }
            btnClassNames="btn btn--primary cover"
            cancelBtnClassNames="btn btn--outline cover"
            loadingText="Approving..."
            handler={handleApprove}
            isLoading={isApproving}
            width={520}
          >
            <div style={{ textAlign: "center", marginBottom: "16px" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  backgroundColor: "#F3F1FC",
                  border: "1px solid #E4DAFC",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px",
                }}
              >
                <FontAwesomeIcon
                  icon="check"
                  style={{ color: "#7647EE", fontSize: "18px" }}
                />
              </div>
              <h4
                className="font-weight-bold mb-1"
                style={{ fontSize: "20px", color: "#15112D" }}
              >
                Confirm Return Approval
              </h4>
              <p
                className="mb-0"
                style={{ fontSize: "13px", color: "#696677" }}
              >
                Request <strong>#{returnDetails?.request_id || id}</strong> •
                Order <strong>#{orderId}</strong>
              </p>
            </div>

            <div
              style={{
                backgroundColor: "#F3F1FC",
                padding: "16px 20px",
                borderRadius: "12px",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                fontSize: "13px",
                lineHeight: "1.5",
                color: "#15112D",
              }}
            >
              <div className="d-flex align-items-start" style={{ gap: "10px" }}>
                <span
                  style={{
                    color: "#7647EE",
                    fontSize: "18px",
                    lineHeight: "1.2",
                  }}
                >
                  •
                </span>
                <div>
                  <strong>Restock Inventory:</strong>{" "}
                  {returnDetails?.items?.reduce(
                    (sum, item) => sum + (Number(item?.quantity) || 0),
                    0,
                  ) || 1}{" "}
                  unit
                  {(returnDetails?.items?.reduce(
                    (sum, item) => sum + (Number(item?.quantity) || 0),
                    0,
                  ) || 1) > 1
                    ? "s"
                    : ""}{" "}
                  will be immediately returned to{" "}
                  <strong>{returnDetails?.store?.name || "store"}</strong>{" "}
                  sellable stock.
                </div>
              </div>

              <div className="d-flex align-items-start" style={{ gap: "10px" }}>
                <span
                  style={{
                    color: "#7647EE",
                    fontSize: "18px",
                    lineHeight: "1.2",
                  }}
                >
                  •
                </span>
                <div>
                  <strong>Process Refund:</strong> A refund of{" "}
                  <strong style={{ color: "#7647EE" }}>
                    {formatCurrency({
                      value: refundableAmount,
                      currencyCode,
                    })}
                  </strong>{" "}
                  will be issued back via original payment method
                  {Number(deliveryFee) > 0
                    ? `, including a ${formatCurrency({
                        value: deliveryFee,
                        currencyCode,
                      })} delivery fee refund`
                    : ""}
                  {Number(serviceCharge) > 0
                    ? `, including a ${formatCurrency({
                        value: serviceCharge,
                        currencyCode,
                      })} service charge refund`
                    : ""}
                  .
                </div>
              </div>
            </div>

            <p
              className="text-center mt-3 mb-0"
              style={{ fontSize: "12px", color: "#718096" }}
            >
              This action directly updates Inventory stock, Sales record and
              Accounting ledger.
            </p>
          </secondaryComponents.ConfirmDialog>

          <secondaryComponents.RejectRequestModal
            isActive={isRejectModalOpen}
            closeModal={() => setIsRejectModalOpen(false)}
            title="Reject Return Request"
            description="Rejecting this return request will decline the request without altering inventory stock or financial refund balances."
            placeholder="Enter reason for declining this return request..."
            onConfirm={handleReject}
            isLoading={isRejecting}
          />
        </div>
      </secondaryComponents.RequestLoaderTrigger>
    </secondaryComponents.MetaSetter>
  );
};

export default ReturnReview;
